/**
 * apps/web :: lib/billing-provider-api
 *
 * Typed wrapper around `GET /api/billing/provider` (ROADS-451 PR-3).
 * The backend exposes the active Merchant-of-Record at runtime so the
 * FE can route checkout + upgrade flows to the correct lane:
 *
 *   - `stripe` — legacy direct-merchant lane (existing /api/checkout/*).
 *   - `paddle` — Merchant-of-Record cutover; checkout flows use
 *                /api/billing/paddle/*.
 *
 * The endpoint is anonymous (no `rb_session` cookie required) so the FE
 * can call it before authentication. The shared `apiFetch` is still the
 * right primitive — it handles the base URL, JSON parsing, and
 * X-Request-Id capture for the error reporter.
 *
 * Cutover policy lives server-side (env flag `BILLING_PROVIDER`); the
 * FE simply trusts the wire response. A future PR will swap the
 * one-shot call for a cached context provider (TODO ROADS-451 PR-5).
 */
import { apiFetch } from "@/lib/api";

/** Closed two-value union — mirrors the backend `BillingProvider` enum. */
export type BillingProvider = "stripe" | "paddle";

/** Wire shape returned by `GET /api/billing/provider`. */
export interface BillingProviderResponse {
  provider: BillingProvider;
}

/**
 * Fetch the active billing provider. Throws `ApiError` on any non-2xx
 * — callers should be ready to fall back to the Stripe lane on
 * transient failures, since the legacy `/api/checkout/*` route remains
 * available throughout the cutover window.
 */
export async function getBillingProvider(): Promise<BillingProvider> {
  const resp = await apiFetch<BillingProviderResponse>("/api/billing/provider");
  return resp.provider;
}
